/**
 * 红色商务模板 - 详情页（v3）
 * 
 * 设计核心理念：专业文档/报告风格
 * - 全宽布局，无侧边栏
 * - 信息卡片式清晰分段
 * - 联系信息独立高亮
 */

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/templates/red/components/Navbar";
import Footer from "@/templates/red/components/Footer";
import {
  getListingDetail,
  getListings,
  type ListingItem,
} from "@/utils/api";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function RedListingDetail() {
  const { id } = useParams<{ id: string }>();
  const { siteId, loading: siteLoading } = useSite();
  const { siteConfig } = useSiteConfig();

  const [listing, setListing] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [relatedListings, setRelatedListings] = useState<ListingItem[]>([]);
  const [showPhone, setShowPhone] = useState(false);

  useEffect(() => {
    if (!id || siteLoading || !siteId) return;
    setLoading(true);
    getListingDetail(id)
      .then((data) => setListing(data))
      .catch(() => window.location.replace("/category"))
      .finally(() => setLoading(false));
  }, [id, siteId, siteLoading]);

  useEffect(() => {
    if (loading || !listing || !siteId) return;
    getListings({ page_size: 8 })
      .then((data) => {
        setRelatedListings(
          (data.list || []).filter((l) => l.id !== id).slice(0, 4)
        );
      })
      .catch(console.error);
  }, [loading, listing, id, siteId]);

  if (loading) return <LoadingSkeleton />;
  if (!listing) return null;

  const images = listing.images?.length > 0 ? listing.images : [];
  const hasContact = listing.contact_phone || listing.contact_wechat;

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <Navbar />

      {/* pt-20 给固定导航栏让位 */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-20 pb-6">
        {/* 面包屑 */}
        <nav className="flex items-center gap-1.5 text-xs text-gray-400 mb-4">
          <Link to="/" className="hover:text-red-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-[10px]" />
          <Link to="/category" className="hover:text-red-600 transition-colors">全部分类</Link>
          {listing.category_name && (
            <>
              <i className="ri-arrow-right-s-line text-[10px]" />
              <span>{listing.category_name}</span>
            </>
          )}
          <i className="ri-arrow-right-s-line text-[10px]" />
          <span className="text-gray-600 truncate">{listing.title}</span>
        </nav>

        {/* 主卡片 */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* 标题 */}
          <div className="px-6 sm:px-8 pt-5 pb-4 border-b border-gray-100">
            <div className="flex items-center gap-2 mb-2.5 flex-wrap">
              {listing.is_pinned && (
                <span className="px-2 py-0.5 bg-red-600 text-white text-[11px] font-bold rounded flex items-center gap-1">
                  <i className="ri-pushpin-line text-[10px]" />置顶
                </span>
              )}
              {listing.category_name && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[11px] rounded">{listing.category_name}</span>
              )}
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800 leading-snug">{listing.title}</h1>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-xs text-gray-400">
              <span><i className="ri-time-line mr-1 text-gray-300" />{formatDate(listing.created_at)}</span>
              <span><i className="ri-eye-line mr-1 text-gray-300" />{listing.view_count} 次浏览</span>
              <span><i className="ri-refresh-line mr-1 text-gray-300" />{formatTimeSince(listing.updated_at || listing.created_at)}</span>
            </div>
          </div>

          {/* 联系信息高亮区 */}
          <div className="px-6 sm:px-8 py-4 bg-red-50/30 border-b border-red-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center text-red-600 shrink-0">
                  <i className="ri-user-3-line text-lg" />
                </div>
                <div>
                  <p className="font-bold text-gray-800">{listing.contact_name || "匿名用户"}</p>
                  {listing.company_name && !siteConfig?.hide_company_name && (
                    <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                      <i className="ri-building-line text-[11px]" />{listing.company_name}
                    </p>
                  )}
                  {listing.address && (
                    <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                      <i className="ri-map-pin-line text-[11px]" />{listing.address}
                    </p>
                  )}
                </div>
              </div>

              {hasContact && (
                <div className="flex items-center gap-2.5 flex-wrap">
                  {listing.contact_phone && (
                    showPhone ? (
                      <a href={`tel:${listing.contact_phone}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <i className="ri-phone-line" />{listing.contact_phone}
                      </a>
                    ) : (
                      <button onClick={() => setShowPhone(true)}
                        className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors cursor-pointer"
                      >
                        <i className="ri-phone-line" />查看电话
                      </button>
                    )
                  )}
                  {listing.contact_wechat && (
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(listing.contact_wechat).catch(() => {});
                        alert("微信号已复制：" + listing.contact_wechat);
                      }}
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-green-50 text-green-700 text-sm font-semibold rounded-lg border border-green-200 hover:bg-green-100 transition-colors cursor-pointer"
                    >
                      <i className="ri-wechat-line" />复制微信
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 详细介绍 */}
          <div className="px-6 sm:px-8 py-5 border-b border-gray-100">
            <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 bg-red-600 rounded-full" />
              详细介绍
            </h2>
            <div className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
              {listing.content || "暂无详细描述。"}
            </div>
          </div>

          {/* 图片展示 */}
          {images.length > 0 && (
            <div className="px-6 sm:px-8 py-5 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-1 h-5 bg-red-600 rounded-full" />
                相关图片
                <span className="text-xs text-gray-400 font-normal">({images.length}张)</span>
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {images.map((img: string, i: number) => (
                  <div key={i} className="rounded-lg overflow-hidden border border-gray-100 bg-gray-50 aspect-[4/3]">
                    <img src={img} alt={`图${i + 1}`} className="w-full h-full object-contain" loading="lazy" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 安全提示 */}
          <div className="px-6 sm:px-8 py-3.5 bg-gray-50/50">
            <div className="flex items-start gap-2 text-xs text-gray-400">
              <i className="ri-shield-check-line text-red-400 mt-0.5" />
              <span>交易时请核实对方资质，谨防诈骗。如发现虚假信息，请及时举报。</span>
            </div>
          </div>
        </div>

        {/* 相关推荐 */}
        {relatedListings.length > 0 && (
          <div className="mt-7">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <span className="w-1 h-5 bg-red-600 rounded-full" />
                相关推荐
              </h2>
              <Link to="/category" className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center gap-1">
                查看更多 <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {relatedListings.map((item) => (
                <Link key={item.id} to={`/listing/${item.id}`}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:border-red-300 hover:shadow-sm transition-all group"
                >
                  <div className="aspect-[4/3] bg-gray-100 overflow-hidden flex items-center justify-center">
                    {item.images?.[0] ? (
                      <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <i className="ri-image-line text-gray-300 text-2xl" />
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-medium text-gray-800 line-clamp-1 group-hover:text-red-600 transition-colors">{item.title}</h3>
                    <div className="flex items-center justify-between mt-1.5 text-[11px] text-gray-400">
                      <span>{item.category?.name || "未分类"}</span>
                      <span>{formatShortDate(item.created_at)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* 返回按钮 */}
        <div className="mt-7 text-center pb-8">
          <Link to="/category"
            className="inline-flex items-center gap-1.5 px-6 py-2.5 bg-white border border-gray-200 text-gray-600 text-sm rounded-lg hover:border-red-300 hover:text-red-600 transition-colors"
          >
            <i className="ri-arrow-left-line" />返回列表
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit" });
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}-${d.getDate()}`;
}

function formatTimeSince(dateStr: string): string {
  const d = new Date(dateStr);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days === 0) return "今天更新";
  if (days === 1) return "昨天更新";
  if (days < 7) return `${days}天前更新`;
  return `${d.getMonth() + 1}月${d.getDate()}日更新`;
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-20 pb-6 animate-pulse">
        <div className="w-48 h-3 bg-gray-200 rounded mb-4" />
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 sm:px-8 pt-5 pb-4 space-y-3">
            <div className="w-3/4 h-7 bg-gray-200 rounded" />
            <div className="w-1/3 h-4 bg-gray-100 rounded" />
          </div>
          <div className="px-6 sm:px-8 py-4 flex items-center gap-3">
            <div className="w-11 h-11 bg-gray-200 rounded-full" />
            <div className="space-y-1.5">
              <div className="w-20 h-4 bg-gray-200 rounded" />
              <div className="w-32 h-3 bg-gray-100 rounded" />
            </div>
          </div>
          <div className="px-6 sm:px-8 py-5 space-y-2">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="w-full h-4 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
